/**
 * V2批量打印请求构造器，把旧打印页面参数转换为后端JSON契约。
 */
import { v4 as uuidv4 } from 'uuid'

const INTERNAL_PARAMETER_KEYS = new Set([
  'sessionId', 'printSource', 'uploadJobFileHistoryParam', 'printCallBackUrl',
  'designMode', 'documentType', 'email_to', 'email_cc', 'email_title', 'email_content'
])

const BUSINESS_UUID_KEYS = [
  'JOB_UUID', 'multipleJobUuid', 'BILLING_UUID', 'BILLPAYMENT_UUID', 'INVOICE_UUID', 'cargoDocUuid',
  'DOC_UUID', 'CONTAINER_UUID', 'TRUCKING_UUID', 'WAREHOUSE_UUID', 'BILLINGNO'
]

function readBusinessUuids(source) {
  const value = source === undefined || source === null ? '' : source
  return unwrapQuotedCsv(value)
    .split(',')
    .map(item => item.trim())
    .filter(item => item && item !== 'null' && item !== 'undefined')
}

function selectBusinessUuids(parameters, form) {
  let firstSelection = null
  for (const key of BUSINESS_UUID_KEYS) {
    const uuids = readBusinessUuids(parameters[key] !== undefined ? parameters[key] : form[key])
    if (uuids.length === 0) continue
    if (uuids.length > 1) return { key, uuids }
    if (!firstSelection) firstSelection = { key, uuids }
  }
  return firstSelection || { key: '', uuids: [''] }
}

function isBatchKey(key, batchKey) {
  if (!batchKey) return false
  return normalizeUuidKey(key) === normalizeUuidKey(batchKey)
}

function isBatchListKey(key, batchKey) {
  if (!batchKey) return false
  return normalizeUuidKey(key) === `${normalizeUuidKey(batchKey)}list`
}

function isBatchUuidKey(key, batchKey) {
  if (isBatchKey(key, batchKey) || isBatchListKey(key, batchKey)) return true
  // 销售查询的旧协议用 JOB_UUID 占位、multipleJobUuid 携带全量；V2拆单时两者都要逐单替换。
  return normalizeUuidKey(batchKey) === 'multiplejobuuid' &&
    ['jobuuid'].includes(normalizeUuidKey(key))
}

function normalizeUuidKey(key) {
  return String(key).replace(/[_-]/g, '').toLowerCase()
}

/**
 * 去掉旧打印参数外层的单引号；正常值原样返回。
 */
function unwrapQuotedValue(value) {
  const text = String(value).trim()
  return text.length >= 2 && text.startsWith("'") && text.endsWith("'")
    ? text.slice(1, -1).trim()
    : text
}

/**
 * 还原旧表单数组参数转换成的 CSV，例如 'a','b' 转成 a,b。
 */
function unwrapQuotedCsv(value) {
  return String(value)
    .split(',')
    .map(unwrapQuotedValue)
    .join(',')
}

function normalizeParameters(form) {
  const raw = typeof form.___PARAMSTRS === 'string' ? form.___PARAMSTRS : ''
  const parameters = {}
  raw.split('&').forEach(entry => {
    if (!entry) return
    const index = entry.indexOf('=')
    if (index <= 0) return
    const key = entry.slice(0, index)
    let value = entry.slice(index + 1)
    if (value && /%[0-9A-Fa-f]{2}/.test(value)) {
      try {
        value = decodeURIComponent(value)
      } catch (e) {
        // 旧页面可能传入不完整的编码，解码失败时保留原始值。
      }
    }
    parameters[key] = unwrapQuotedCsv(value)
  })
  return parameters
}

function cloneParameters(parameters, batchKey, batchUuid = '') {
  const result = {}
  Object.keys(parameters).forEach(key => {
    if (INTERNAL_PARAMETER_KEYS.has(key)) return
    if (batchKey && isBatchUuidKey(key, batchKey)) {
      result[key] = batchUuid
      return
    }
    const value = parameters[key]
    result[key] = Array.isArray(value) ? value.join(',') : value
  })
  return result
}

function makeIdempotencyKey(templateType) {
  // 幂等键只代表一次打印提交动作；用户再次点击打印时允许生成新任务。
  return `${templateType}:${uuidv4()}`
}

export class PrintTaskV2RequestBuilder {
  static isLegacyEnabled(configValue = '', envValue = '') {
    return String(configValue || envValue || 'v2').trim().toLowerCase() === 'legacy'
  }

  static build({
    template,
    templateType,
    form,
    exportType,
    sourcePage = '',
    printSource = ''
  }) {
    if (!template || !templateType || !form || !exportType) {
      throw new Error('template, templateType, form and exportType are required')
    }
    const parameters = normalizeParameters(form)
    const selection = selectBusinessUuids(parameters, form)
    const businessUuids = selection.uuids
    const mergeToZip = businessUuids.length > 1
    // 批量打印沿用 legacy：基础名只传模板名，业务号由后端按每个打印单元重算。
    const fileName = template.name || 'print'
    const items = businessUuids.map((businessUuid, index) => ({
      businessType: templateType.templateType,
      businessUuid,
      parameters: cloneParameters(
        parameters,
        selection.uuids.length > 1 ? selection.key : '',
        businessUuid
      )
    }))

    return {
      idempotencyKey: makeIdempotencyKey(template.templateType),
      template: {
        templateUuid: template.uuid,
        templateName: template.name || '',
        storageFileId: template.documentId,
        templateType: template.templateType,
        schemaType: templateType.schemaType,
        lob: unwrapQuotedValue(parameters.LOB || ''),
        remarks: template.remarks || ''
      },
      output: {
        exportType,
        multiplePrint: mergeToZip,
        mergeToZip,
        fileName
      },
      context: {
        officeId: form.OFFICE_ID,
        language: form.LANGUAGE,
        zone: form.zone,
        sourcePage,
        metaData: { printSource: printSource || parameters.printSource || '' }
      },
      items
    }
  }
}
